/**
 * Octopus IoT Platform - Operations Activity Page
 * Fase C/D da integração LAB → principal.
 *
 * Superfície de OPERAÇÃO da máquina de atividade server-side: estado corrente
 * (carregando/descarregando/parado/viagem), ETA até o porto e ciclo da viagem.
 * TUDO read-only: lê o estado que o worker (lab_activity_worker) já computou.
 * Polling de 15s (abaixo do anti-storm de 30/60s).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, Ship, RefreshCw, Loader2, Anchor, Navigation, Fuel, Clock } from 'lucide-react';
import { toast } from 'sonner';

import operationsApi from '@/services/operationsApi';
import { activityApi } from '@/services/activityApi';
import EtaWidget from '@/components/EtaWidget';
import WorkHoldBadge from '@/components/monitor/WorkHoldBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useTimezone } from '@/contexts/TimezoneContext';

const POLL_MS = 15000;

const ACTIVITY_LABEL = {
  carregando: 'Carregando',
  descarregando: 'Descarregando',
  parado: 'Parado',
  viagem: 'Em deslocamento',  // base; o sufixo (carregado)/(vazio) é anexado em activityLabel via cargoOf
};

const ACTIVITY_BADGE = {
  carregando: 'bg-amber-500 hover:bg-amber-500 text-black border-transparent',
  descarregando: 'bg-sky-500 hover:bg-sky-500 text-white border-transparent',
  parado: 'bg-muted text-muted-foreground border-transparent',
  viagem: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent',
};

const MOTOR_LABEL = { engine1: 'Draga', port: 'Bombordo', starboard: 'Boreste', center: 'Central', main: 'Principal' };
const motorLabel = (path) => {
  const k = String(path).replace('propulsion.', '').replace('.fuel.rate', '').replace('.revolutions', '');
  return MOTOR_LABEL[k] || k;
};

const fmtDuration = (s) => {
  if (s == null || !Number.isFinite(Number(s))) return '—';
  const total = Math.round(Number(s));
  const hh = Math.floor(total / 3600);
  const mm = Math.floor((total % 3600) / 60);
  if (hh > 0) return `${hh}h ${mm}min`;
  if (mm > 0) return `${mm}min`;
  return `${total}s`;
};

const fmtL = (l) =>
  l == null || !Number.isFinite(Number(l)) ? '—' : `${Math.round(Number(l)).toLocaleString('pt-BR')} L`;

// D-04 (revisado pelo dono, 2026-06-29): a classificação do deslocamento é por ESTADO DE
// CARGA, não por direção geográfica. Carga só ocorre em ZONA DE CARREGAMENTO (leito do rio);
// descarga só em ZONA DE DESCARGA. Em trânsito após carregar ⇒ CARREGADO; após descarregar
// ⇒ VAZIO. O badge exibe o sufixo (carregado)/(vazio). `cargo_state` vem pronto do backend —
// só exibição, não recompute (INV-UI-TRAIL-001 preservado). Fallback para `subdir` legado
// (ida/volta) durante a janela de deploy até todos os docs migrarem (Phase 11).
const LEGACY_CARGO = { ida: 'carregado', volta: 'vazio' };
const cargoOf = (o) => o?.cargo_state || LEGACY_CARGO[o?.subdir] || null;

const activityLabel = (state) => {
  if (!state?.activity) return 'Sem dados';
  const base = ACTIVITY_LABEL[state.activity] || state.activity;
  const cs = cargoOf(state);
  if (state.activity === 'viagem' && cs) return `${base} (${cs})`;
  return base;
};

const STAGE_LABEL = {
  carregando: 'Carregando',
  descarregando: 'Descarregando',
  parado: 'Parado',
  viagem_carregado: 'Em deslocamento (carregado)',
  viagem_vazio: 'Em deslocamento (vazio)',
  viagem: 'Em deslocamento',  // fallback sem cargo_state
};
const stageLabel = (row) => {
  const cs = cargoOf(row);
  const key = row.activity === 'viagem' && cs ? `viagem_${cs}` : row.activity;
  return STAGE_LABEL[key] || key || '—';
};

// D-05: conjunto de motivos de parada (allowlist espelha o backend — plano 03).
const STOP_REASON_OPTIONS = [
  { value: 'clima', label: 'Clima' },
  { value: 'manutencao', label: 'Manutenção' },
  { value: 'quebra_inesperada', label: 'Quebra Inesperada' },
  { value: 'parada_programada', label: 'Parada Programada' },
  { value: 'outro', label: 'Outro' },
];

const STOP_REASON_LABEL = {
  clima: 'Clima',
  manutencao: 'Manutenção',
  quebra_inesperada: 'Quebra Inesperada',
  parada_programada: 'Parada Programada',
  outro: 'Outro',
  // B4: anotação automática do worker quando parado dentro de zona de apoio
  // (reason_text carrega o nome do ponto; o despachante pode sobrescrever).
  apoio: 'Ponto de apoio',
};

export default function OperationsActivityPage() {
  const { formatDateTime } = useTimezone();

  const [equipments, setEquipments] = useState([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('');
  const [state, setState] = useState(null);
  const [summary, setSummary] = useState(null);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);

  // D-05/D-06: estado local do formulário de motivo de parada.
  const [stopReason, setStopReason] = useState('');
  const [stopReasonText, setStopReasonText] = useState('');
  const [savingReason, setSavingReason] = useState(false);
  // true quando o usuário clicou "Outro motivo" a partir do banner de sugestão de manutenção.
  const [showReasonSelector, setShowReasonSelector] = useState(false);

  const loadEquipments = useCallback(async () => {
    try {
      const res = await operationsApi.getEquipments();
      const data = res.data || [];
      setEquipments(data);
      setSelectedEquipmentId((prev) => prev || (data[0]?.id ?? ''));
    } catch (e) {
      console.error('[Activity] erro ao carregar embarcações:', e);
      toast.error('Erro ao carregar a lista de embarcações.');
    }
  }, []);

  const loadActivity = useCallback(async (equipmentId, withSpinner = false) => {
    if (!equipmentId) return;
    if (withSpinner) setLoading(true);
    try {
      const res = await activityApi.getActivity(equipmentId);
      setState(res.data || null);
    } catch (e) {
      console.warn('[Activity] erro ao carregar atividade:', e?.response?.status || e);
    } finally {
      if (withSpinner) setLoading(false);
    }
  }, []);

  // Relatórios (apontamento por etapa + viagens): histórico/agregado, recarrega ao
  // trocar de embarcação e no "Atualizar" — não faz parte do polling de 15s.
  const loadReports = useCallback(async (equipmentId) => {
    if (!equipmentId) return;
    const [sumR, tripR] = await Promise.allSettled([
      activityApi.getSummary(equipmentId),
      activityApi.getTrips(equipmentId, { limit: 20 }),
    ]);
    setSummary(sumR.status === 'fulfilled' ? sumR.value.data || null : null);
    setTrips(tripR.status === 'fulfilled' ? tripR.value.data?.trips || [] : []);
  }, []);

  // D-05: salvar motivo de parada via PATCH stop-reason.
  const handleSaveReason = useCallback(async () => {
    if (!stopReason) return;
    setSavingReason(true);
    try {
      const payload = { reason: stopReason };
      if (stopReason === 'outro' && stopReasonText.trim()) {
        payload.reason_text = stopReasonText.trim();
      }
      await activityApi.setStopReason(selectedEquipmentId, payload);
      await loadActivity(selectedEquipmentId);
      setStopReason('');
      setStopReasonText('');
      setShowReasonSelector(false);
      toast.success('Motivo de parada registrado.');
    } catch (e) {
      console.error('[Activity] erro ao salvar motivo de parada:', e);
      toast.error('Erro ao salvar motivo de parada.');
    } finally {
      setSavingReason(false);
    }
  }, [stopReason, stopReasonText, selectedEquipmentId, loadActivity]);

  // D-06: confirmar sugestão automática de manutenção (parado longo).
  const handleConfirmManutencao = useCallback(async () => {
    setSavingReason(true);
    try {
      await activityApi.setStopReason(selectedEquipmentId, { reason: 'manutencao' });
      await loadActivity(selectedEquipmentId);
      toast.success('Manutenção confirmada.');
    } catch (e) {
      console.error('[Activity] erro ao confirmar manutenção:', e);
      toast.error('Erro ao confirmar manutenção.');
    } finally {
      setSavingReason(false);
    }
  }, [selectedEquipmentId, loadActivity]);

  useEffect(() => {
    loadEquipments();
  }, [loadEquipments]);

  useEffect(() => {
    if (!selectedEquipmentId) return;
    setState(null);
    setSummary(null);
    setTrips([]);
    // Resetar formulário de motivo de parada ao trocar de embarcação.
    setStopReason('');
    setStopReasonText('');
    setShowReasonSelector(false);
    loadActivity(selectedEquipmentId, true);
    loadReports(selectedEquipmentId);
    const id = setInterval(() => loadActivity(selectedEquipmentId), POLL_MS);
    return () => clearInterval(id);
  }, [selectedEquipmentId, loadActivity, loadReports]);

  // Resetar formulário quando a atividade sair de "parado" (ex.: barco saiu e voltou a viagem).
  useEffect(() => {
    if (state?.activity !== 'parado') {
      setStopReason('');
      setStopReasonText('');
      setShowReasonSelector(false);
    }
  }, [state?.activity]);

  const cycle = state?.cycle || null;
  const byMotor = useMemo(
    () => Object.entries(state?.fuel_by_motor || {}).sort((a, b) => (b[1] || 0) - (a[1] || 0)),
    [state],
  );

  return (
    <div className="page-container space-y-6" data-testid="operations-activity-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="pl-12 md:pl-0">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Activity className="w-6 h-6 text-primary" />
            Atividade da Operação
          </h1>
          <p className="text-sm text-muted-foreground">
            Estado corrente, ETA até o porto e ciclo da viagem (server-authoritative)
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => { loadActivity(selectedEquipmentId, true); loadReports(selectedEquipmentId); }}
          disabled={loading || !selectedEquipmentId}
          className="gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Atualizar
        </Button>
      </div>

      {/* Seletor de embarcação */}
      <Card>
        <CardContent className="p-4">
          <div className="max-w-md space-y-2">
            <Label className="flex items-center gap-2">
              <Ship className="w-4 h-4 text-muted-foreground" />
              Embarcação
            </Label>
            <Select value={selectedEquipmentId} onValueChange={setSelectedEquipmentId}>
              <SelectTrigger data-testid="activity-equipment">
                <SelectValue placeholder="Selecione uma embarcação" />
              </SelectTrigger>
              <SelectContent>
                {equipments.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name} {e.tag ? `(${e.tag})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estado corrente */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Anchor className="h-4 w-4 text-primary" />
              Estado corrente
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <Badge className={ACTIVITY_BADGE[state?.activity] || ACTIVITY_BADGE.parado}>
                {activityLabel(state)}
              </Badge>
              {state?.since && (
                <span className="text-xs text-muted-foreground">desde {formatDateTime(state.since)}</span>
              )}
            </div>
            {/* RC-2: histerese de saída em curso — countdown do fechamento retroativo */}
            <WorkHoldBadge workHold={state?.work_hold} />
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Tempo na etapa:</span>
                <span className="font-medium">{fmtDuration(state?.duration_s)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Fuel className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Combustível:</span>
                <span className="font-medium">{fmtL(state?.fuel_l)}</span>
              </div>
            </div>
            {byMotor.length > 0 && (
              <div className="border-t pt-2">
                <p className="text-xs text-muted-foreground mb-1">Consumo por motor (etapa atual)</p>
                <div className="flex flex-wrap gap-2">
                  {byMotor.map(([path, liters]) => (
                    <Badge key={path} variant="outline" className="font-normal">
                      {motorLabel(path)}: {fmtL(liters)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {/* D-05/D-06: Motivo de parada — visível apenas quando activity === 'parado' */}
            {state?.activity === 'parado' && (
              <div className="border-t pt-3 space-y-3">
                {state?.stop_reason ? (
                  /* Motivo já informado: exibição read-only */
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Motivo de parada</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">
                        {STOP_REASON_LABEL[state.stop_reason.reason] || state.stop_reason.reason}
                      </Badge>
                      {state.stop_reason.reason_text && (
                        <span className="text-sm text-foreground">{state.stop_reason.reason_text}</span>
                      )}
                    </div>
                    {state.stop_reason.by && (
                      <p className="text-xs text-muted-foreground">
                        Por {state.stop_reason.by}
                        {state.stop_reason.at ? ` em ${formatDateTime(state.stop_reason.at)}` : ''}
                      </p>
                    )}
                  </div>
                ) : state?.parado_longo_suggestion && !showReasonSelector ? (
                  /* D-06: Sugestão automática "Manutenção?" para parada longa */
                  <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Barco parado há um tempo — Manutenção?</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        size="sm"
                        onClick={handleConfirmManutencao}
                        disabled={savingReason}
                        className="gap-1"
                      >
                        {savingReason && <Loader2 className="h-3 w-3 animate-spin" />}
                        Confirmar Manutenção
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowReasonSelector(true)}
                        disabled={savingReason}
                      >
                        Outro motivo
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* D-05: Seletor de motivo de parada */
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Informar motivo de parada</p>
                    <Select value={stopReason} onValueChange={setStopReason}>
                      <SelectTrigger className="w-full max-w-xs">
                        <SelectValue placeholder="Selecione o motivo" />
                      </SelectTrigger>
                      <SelectContent>
                        {STOP_REASON_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {stopReason === 'outro' && (
                      <input
                        type="text"
                        className="flex h-9 w-full max-w-xs rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        placeholder="Descreva o motivo..."
                        value={stopReasonText}
                        onChange={(e) => setStopReasonText(e.target.value)}
                        maxLength={200}
                      />
                    )}
                    <Button
                      size="sm"
                      onClick={handleSaveReason}
                      disabled={!stopReason || savingReason || (stopReason === 'outro' && !stopReasonText.trim())}
                      className="gap-1"
                    >
                      {savingReason && <Loader2 className="h-3 w-3 animate-spin" />}
                      Salvar motivo
                    </Button>
                  </div>
                )}
              </div>
            )}

            {!state?.activity && (
              <p className="text-sm text-muted-foreground">
                Nenhum estado de atividade para esta embarcação ainda. A máquina de atividade
                roda para embarcações com a bomba de draga e geofences classificadas.
              </p>
            )}
          </CardContent>
        </Card>

        {/* ETA */}
        <EtaWidget equipmentId={selectedEquipmentId} />
      </div>

      {/* Ciclo da viagem */}
      {cycle?.id && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Navigation className="h-4 w-4 text-primary" />
              Ciclo da viagem
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <span className="text-muted-foreground">Ciclo: <b className="text-foreground">{cycle.id}</b></span>
              {cycle.destination && (
                <span className="text-muted-foreground">Destino: <b className="text-foreground">{cycle.destination}</b></span>
              )}
              {cycle.started_at && (
                <span className="text-muted-foreground">Início: <b className="text-foreground">{formatDateTime(cycle.started_at)}</b></span>
              )}
            </div>
            {Array.isArray(cycle.stages) && cycle.stages.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {cycle.stages.map((stage, i) => (
                  <Badge key={`${stage}-${i}`} variant="secondary" className="font-normal">
                    {ACTIVITY_LABEL[stage] || stage.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Apontamento por etapa (combustível/tempo por carga, descarga, viagem) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Apontamento por etapa</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Etapa</TableHead>
                <TableHead className="text-right">Ocorrências</TableHead>
                <TableHead className="text-right">Tempo</TableHead>
                <TableHead className="text-right">Combustível</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(summary?.by_activity || []).map((row, i) => (
                <TableRow key={`${row.activity}-${cargoOf(row) || ''}-${i}`}>
                  <TableCell>{stageLabel(row)}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{row.count ?? '—'}</TableCell>
                  <TableCell className="text-right">{fmtDuration(row.duration_s)}</TableCell>
                  <TableCell className="text-right">{fmtL(row.fuel_l)}</TableCell>
                </TableRow>
              ))}
              {(summary?.by_activity?.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                    Sem segmentos de atividade registrados.
                  </TableCell>
                </TableRow>
              )}
              {(summary?.by_activity?.length ?? 0) > 0 && (
                <TableRow className="font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right text-muted-foreground">{summary?.segment_count ?? '—'}</TableCell>
                  <TableCell className="text-right">{fmtDuration(summary?.total_duration_s)}</TableCell>
                  <TableCell className="text-right">{fmtL(summary?.total_fuel_l)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Viagens (ciclos fechados — detecção de início/fim de descarga) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Viagens</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ciclo</TableHead>
                <TableHead>Destino</TableHead>
                <TableHead>Início</TableHead>
                <TableHead>Fim</TableHead>
                <TableHead className="text-right">Duração</TableHead>
                <TableHead className="text-right">Combustível</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trips.map((trip, i) => (
                <TableRow key={trip.cycle_id || i}>
                  <TableCell className="font-medium">{trip.cycle_id || '—'}</TableCell>
                  <TableCell>{trip.destination || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{trip.started_at ? formatDateTime(trip.started_at) : '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{trip.ended_at ? formatDateTime(trip.ended_at) : '—'}</TableCell>
                  <TableCell className="text-right">{fmtDuration(trip.duration_s)}</TableCell>
                  <TableCell className="text-right">{fmtL(trip.fuel_l)}</TableCell>
                </TableRow>
              ))}
              {trips.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    Nenhuma viagem fechada registrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
